import logger from '../diagnostics/logging/logger';
import {
    ApolloClient,
    HttpLink,
    ApolloLink,
    Observable,
    split,
    from,
} from '@apollo/client';
import { InMemoryCache } from '@apollo/client/cache';
import { onError } from '@apollo/client/link/error';
import { getMainDefinition } from '@apollo/client/utilities';
import { setContext } from '@apollo/client/link/context';
import { apiBaseUrl } from '../config/index';
import generateId from '../tools/idGenerator';
import * as types from '../actions/actionTypes';
import store from 'store';
import ReadErrorUnknownCauseMessage from '../components/ui/messages/wordage/errors/ReadErrorUnknownCauseMessage';
import WriteRejectedErrorSafeToRetryMessage from '../components/ui/messages/wordage/errors/WriteRejectedErrorSafeToRetryMessage';
import WriteUnknownResultErrorSafeToRetryMessage from '../components/ui/messages/wordage/errors/WriteUnknownResultErrorSafeToRetryMessage';
import WriteUnknownResultErrorCheckBeforeRetryMessage from '../components/ui/messages/wordage/errors/WriteUnknownResultErrorCheckBeforeRetryMessage';
import { sessionId } from './session';
import { buildGraphQlUri, diagnosticHeadersLink } from './graphQlDiagnostics';
import { authenticationService } from '_legacy/auth/AuthenticationService';
import { createClient } from 'graphql-ws';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { versionReceived } from '_legacy/actions/versionActions';

let _graphQlClient = undefined;

//code below is largely taken from https://www.apollographql.com/docs/react/advanced/boost-migration.html
const addSessionContextToRequest = async (operation) => {
    const token = await authenticationService.getAccessTokenAsync();
    if (token) {
        const { headers: existingHeaders = {} } = operation.getContext();
        operation.setContext({
            headers: {
                ...existingHeaders,
                authorization: encodeURIComponent(token),
                fortSessionId: sessionId,
            },
        });
    }
};

const sessionContextAddingMiddleware = new ApolloLink(
    (operation, forward) =>
        new Observable((observer) => {
            let handle;
            Promise.resolve(operation)
                .then((oper) => addSessionContextToRequest(oper))
                .then(() => {
                    handle = forward(operation).subscribe({
                        next: observer.next.bind(observer),
                        error: observer.error.bind(observer),
                        complete: observer.complete.bind(observer),
                    });
                })
                .catch((err) => {
                    logger.warn(
                        `[sessionContextMiddleware] Failed to get token for "${operation.operationName}": ${err?.message}`
                    );
                    observer.error(err);
                });

            return () => {
                if (handle) handle.unsubscribe();
            };
        })
);

const createHttpWithWebSocketUpdatesLink = ({
    httpResource,
    webSocketClient,
}) => {
    // using the ability to split links, you can send data to each link
    // depending on what kind of operation is being sent
    return split(
        // split based on operation type
        ({ query }) => {
            const { kind, operation } = getMainDefinition(query);
            return (
                kind === 'OperationDefinition' && operation === 'subscription'
            );
        },
        new GraphQLWsLink(webSocketClient),
        new HttpLink(httpResource)
    );
};

const graphQlClient = () => {
    if (!_graphQlClient) {
        const subscriptionClient = createSocketSubscription();
        const mainLink = ApolloLink.from([
            onError(({ graphQLErrors, networkError, operation, forward }) => {
                const statusCode = networkError && networkError.statusCode;
                const isUnauthorised = statusCode === 401 || statusCode === 403;

                // ── Diagnostic: log every error that reaches onError ──
                const opDefs =
                    operation?.query?.definitions?.filter(
                        (d) => d.kind === 'OperationDefinition'
                    ) || [];
                const opType =
                    opDefs.map((d) => d.operation).join(',') || 'unknown';
                logger.warn(
                    `[onError] operation="${operation.operationName}" type=${opType}` +
                        ` | networkError=${
                            networkError
                                ? `${networkError.name}: ${networkError.message} (status=${statusCode}, code=${networkError.code})`
                                : 'none'
                        }` +
                        ` | graphQLErrors=${
                            graphQLErrors
                                ? graphQLErrors.map((e) => e.message).join('; ')
                                : 'none'
                        }`
                );

                // Subscriptions run in the background, so their failures are not caused by
                // anything the user did. Connection loss is already reported by the
                // "Live Updates: Connection Lost" message (SOCKET_DISCONNECTED), and the
                // WebSocket client keeps reconnecting and resubscribing on its own.
                // Showing a red read-error banner per subscription just produced a
                // stack of banners after inactivity.
                const isSubscription = opDefs.some(
                    (d) => d.operation === 'subscription'
                );
                if (isSubscription) {
                    return;
                }

                // Requests aborted on purpose (e.g. a superseded search) are not failures.
                if (networkError?.name === 'AbortError') {
                    return;
                }

                if (isUnauthorised) {
                    const { alreadyRetried } = operation.getContext();
                    if (alreadyRetried) {
                        logger.warn(
                            'Token refresh retry already attempted, giving up'
                        );
                    } else {
                        return new Observable((observer) => {
                            authenticationService
                                .getAccessTokenAsync()
                                .then((token) => {
                                    if (!token) {
                                        throw new Error(
                                            'Token refresh returned no token during 401 retry'
                                        );
                                    }
                                    const { headers: existingHeaders = {} } =
                                        operation.getContext();
                                    operation.setContext({
                                        alreadyRetried: true,
                                        headers: {
                                            ...existingHeaders,
                                            authorization:
                                                encodeURIComponent(token),
                                            fortSessionId: sessionId,
                                        },
                                    });
                                })
                                .then(() => {
                                    forward(operation).subscribe({
                                        next: observer.next.bind(observer),
                                        error: observer.error.bind(observer),
                                        complete:
                                            observer.complete.bind(observer),
                                    });
                                })
                                .catch((refreshError) => {
                                    logger.warn(
                                        'Token refresh failed during 401 retry: ' +
                                            refreshError.message
                                    );
                                    observer.error(networkError);
                                });
                        });
                    }
                }

                // Retry once on transient network errors (e.g. browser waking
                // from sleep/inactivity). "Failed to fetch" with no statusCode
                // means the request never reached the server, so it is always
                // safe to retry.
                const isTransientNetworkError =
                    networkError instanceof TypeError && !statusCode;

                if (isTransientNetworkError) {
                    const { networkRetryCount = 0 } = operation.getContext();
                    if (networkRetryCount < 1) {
                        logger.warn(
                            `[onError] Transient network error for "${
                                operation.operationName
                            }" — retrying (attempt ${networkRetryCount + 1})`
                        );
                        operation.setContext({
                            networkRetryCount: networkRetryCount + 1,
                        });
                        return forward(operation);
                    }
                }

                if (graphQLErrors) {
                    graphQLErrors.forEach((e) =>
                        logger.error(
                            /* error: */ e,
                            /* props: */ {
                                operationName: operation.operationName,
                            }
                        )
                    );
                }

                if (networkError) {
                    logger.error(
                        /* error: */ networkError,
                        /* props: */ {
                            operationName: operation.operationName,
                            // See https://www.apollographql.com/docs/link/links/http.html#error
                            url: networkError?.response?.url,
                            statusCode: networkError.statusCode,
                            bodyText: networkError.bodyText,
                        }
                    );
                }

                logger.warn(
                    `[onError] ▶ DISPATCHING RED BANNER for "${operation.operationName}" (type=${opType})`
                );
                store.dispatch({
                    type: types.NOTIFICATION_MESSAGE_ADDED,
                    message: _getUserNotificationMessageForError({
                        graphQLErrors,
                        networkError,
                        operation,
                    }),
                });
            }),
            sessionContextAddingMiddleware,
            diagnosticHeadersLink,
            createHttpWithWebSocketUpdatesLink({
                httpResource: {
                    uri: (operation) => buildGraphQlUri(apiBaseUrl, operation),
                    credentials: 'same-origin',
                },
                webSocketClient: subscriptionClient,
            }),
        ]);

        function _getUserNotificationMessageForError({
            graphQLErrors,
            networkError,
            operation,
        }) {
            const operationTypes =
                operation &&
                operation.query &&
                operation.query.definitions.length &&
                operation.query.definitions
                    .filter(
                        (queryDef) => queryDef.kind === 'OperationDefinition'
                    )
                    .map((queryDef) => queryDef.operation);
            const isRead =
                operationTypes &&
                operationTypes.every(
                    (oT) => oT === 'query' || oT === 'subscription'
                );

            const context = operation.getContext();
            const errorHandlingArgs = context && context.errorHandling;

            if (isRead)
                return {
                    typeId: 'READ_ERROR_FOR_UNKNOWN_REASON',
                    getMessageRichContent: (props) => (
                        <ReadErrorUnknownCauseMessage
                            {...props}
                            resultDescription={
                                errorHandlingArgs &&
                                errorHandlingArgs.rejectedResult &&
                                errorHandlingArgs.rejectedResult
                                    .resultDescription &&
                                errorHandlingArgs.rejectedResult.resultDescription()
                            }
                            resolutionToTryBeforeContacting={
                                errorHandlingArgs &&
                                errorHandlingArgs.rejectedResult &&
                                errorHandlingArgs.rejectedResult.resolution &&
                                errorHandlingArgs.rejectedResult.resolution
                                    .tryBeforeContacting &&
                                errorHandlingArgs.rejectedResult.resolution.tryBeforeContacting()
                            }
                        />
                    ),
                };
            else {
                const responseStatus =
                    networkError &&
                    networkError.response &&
                    networkError.response.statusCode;

                const isHttpInvalidRequest =
                    responseStatus >= 400 && responseStatus <= 499;
                const isGraphQlInvalidRequest =
                    graphQLErrors &&
                    graphQLErrors.length &&
                    graphQLErrors.every(
                        (error) =>
                            error &&
                            error.extensions &&
                            error.extensions.code === 'INVALID_REQUEST'
                    );

                if (isHttpInvalidRequest || isGraphQlInvalidRequest)
                    return {
                        typeId: 'WRITE_REJECTED_FOR_UNKNOWN_REASON_RETRY_SAFE',
                        getMessageRichContent: (props) => (
                            <WriteRejectedErrorSafeToRetryMessage
                                {...props}
                                resultDescription={
                                    errorHandlingArgs.rejectedResult &&
                                    errorHandlingArgs.rejectedResult
                                        .resultDescription &&
                                    errorHandlingArgs.rejectedResult.resultDescription()
                                }
                                resolutionToTryBeforeContacting={
                                    errorHandlingArgs &&
                                    errorHandlingArgs.rejectedResult &&
                                    errorHandlingArgs.rejectedResult
                                        .resolution &&
                                    errorHandlingArgs.rejectedResult.resolution
                                        .tryBeforeContacting &&
                                    errorHandlingArgs.rejectedResult.resolution.tryBeforeContacting()
                                }
                            />
                        ),
                    };
                else {
                    const unknownResultErrorHandlingArgs =
                        errorHandlingArgs && errorHandlingArgs.unknownResult;
                    const isRetrySafe = !!(
                        unknownResultErrorHandlingArgs &&
                        unknownResultErrorHandlingArgs.resolution &&
                        unknownResultErrorHandlingArgs.resolution.isSafeToRetry
                    );
                    if (isRetrySafe)
                        return {
                            typeId: 'WRITE_UNKNOWN_RESULT_RETRY_SAFE',
                            getMessageRichContent: (props) => (
                                <WriteUnknownResultErrorSafeToRetryMessage
                                    {...props}
                                    resultDescription={
                                        unknownResultErrorHandlingArgs &&
                                        unknownResultErrorHandlingArgs.resultDescription &&
                                        unknownResultErrorHandlingArgs.resultDescription()
                                    }
                                    resolutionToTryBeforeContacting={
                                        unknownResultErrorHandlingArgs &&
                                        unknownResultErrorHandlingArgs.resolution &&
                                        unknownResultErrorHandlingArgs
                                            .resolution.tryBeforeContacting &&
                                        unknownResultErrorHandlingArgs.resolution.tryBeforeContacting()
                                    }
                                />
                            ),
                        };
                    else
                        return {
                            typeId: 'WRITE_UNKNOWN_RESULT_RETRY_UNSAFE',
                            getMessageRichContent: (props) => (
                                <WriteUnknownResultErrorCheckBeforeRetryMessage
                                    {...props}
                                    resultDescription={
                                        unknownResultErrorHandlingArgs &&
                                        unknownResultErrorHandlingArgs.resultDescription &&
                                        unknownResultErrorHandlingArgs.resultDescription()
                                    }
                                    resolutionToTryBeforeContacting={
                                        unknownResultErrorHandlingArgs &&
                                        unknownResultErrorHandlingArgs.resolution &&
                                        unknownResultErrorHandlingArgs
                                            .resolution.tryBeforeContacting &&
                                        unknownResultErrorHandlingArgs.resolution.tryBeforeContacting()
                                    }
                                />
                            ),
                        };
                }
            }
        }

        const dependencyStartTracker = setContext(() => ({
            requestStartTime: new Date().getTime(),
        }));

        const dependencySuccessTracker = new ApolloLink(
            (operation, forward) => {
                return forward(operation).map((response) => {
                    const currentTime = new Date().getTime();
                    const startTime = operation.getContext().requestStartTime;
                    const requestElapsedTime = currentTime - startTime;

                    // Apollo deals with the operation at a high level, so I've just put some place holders
                    // in for type and url and host.
                    logger.trackDependency(
                        generateId(),
                        'POST',
                        `graph ${operation.operationName}`,
                        '/graph',
                        requestElapsedTime,
                        true,
                        200
                    );

                    return response;
                });
            }
        );

        const dependencyErrorTracker = onError(({ operation }) => {
            const currentTime = new Date().getTime();
            const startTime = operation.getContext().requestStartTime;
            const requestElapsedTime = currentTime - startTime;
            // Apollo deals with the operation at a high level, so I've just put some place holders
            // in for type and url and host.
            logger.trackDependency(
                generateId(),
                'POST',
                `graph ${operation.operationName}`,
                '/graph',
                requestElapsedTime,
                false,
                0
            );
        });

        _graphQlClient = new ApolloClient({
            link: from([
                dependencySuccessTracker,
                dependencyErrorTracker,
                dependencyStartTracker,
                mainLink,
            ]),
            cache: new InMemoryCache(),
        });
    }
    return _graphQlClient;
};

// If the server has been silent for this long, send a ping...
export const KEEP_ALIVE_INTERVAL_MS = 20000;
// ...and treat the socket as dead if no pong arrives within this time
// (e.g. after the laptop wakes from sleep with a half-open TCP connection).
export const PONG_TIMEOUT_MS = 10000;
export const MAX_RETRY_DELAY_MS = 30000;
// 4408 is not treated as fatal by graphql-ws, so the client reconnects.
const PONG_TIMEOUT_CLOSE_CODE = 4408;

// Exponential backoff capped at MAX_RETRY_DELAY_MS, plus 300ms-3s jitter so
// all open tabs do not reconnect at the same moment.
export const getRetryDelayMs = (retries) =>
    Math.min(1000 * 2 ** retries, MAX_RETRY_DELAY_MS) +
    Math.floor(Math.random() * 2700 + 300);

const createSocketSubscription = () => {
    let activeSocket;
    let pongTimeout;

    const handleConnected = (socket, payload, wasRetry) => {
        activeSocket = socket;
        if (wasRetry) {
            console.info('[webSocket] Live updates connection restored');
        }
        store.dispatch({
            type: wasRetry ? types.SOCKET_RECONNECTED : types.SOCKET_CONNECTED,
        });
    };

    const handleDisconnected = (e) => {
        clearTimeout(pongTimeout);
        if (e.wasClean) {
            return;
        }
        store.dispatch({ type: types.SOCKET_DISCONNECTED });
    };

    const handleError = (e) => {
        logger.warn(`[webSocket] ${e?.message ?? e}`);
    };

    const handlePing = (received) => {
        if (received) return;
        // We sent a ping - expect a pong back.
        clearTimeout(pongTimeout);
        pongTimeout = setTimeout(() => {
            if (activeSocket?.readyState === WebSocket.OPEN) {
                logger.warn('[webSocket] Pong timeout, reconnecting');
                activeSocket.close(PONG_TIMEOUT_CLOSE_CODE, 'Pong timeout');
            }
        }, PONG_TIMEOUT_MS);
    };

    const handlePong = (received, payload) => {
        if (!received) return;
        clearTimeout(pongTimeout);
        // Only the server's keep-alive pongs carry the version; replies to our own pings do not.
        if (payload?.version) {
            store.dispatch(versionReceived(payload.version));
        }
    };

    const client = createClient({
        url: `wss://${apiBaseUrl}/graphql`,
        lazy: true,
        keepAlive: KEEP_ALIVE_INTERVAL_MS,
        // Never give up reconnecting: a laptop can sleep or a VPN can drop for far
        // longer than the default 5 attempts (~30s). Once graphql-ws gives up, every
        // active subscription fails permanently until the page is refreshed.
        retryAttempts: Infinity,
        retryWait: (retries) =>
            new Promise((resolve) =>
                setTimeout(resolve, getRetryDelayMs(retries))
            ),
        // The default only retries on close events; also retry other connection
        // problems (e.g. the socket failing to open while offline).
        shouldRetry: () => true,
        connectionParams: async () => {
            let token;
            try {
                token = await authenticationService.getAccessTokenAsync();
            } catch (err) {
                // A throw here makes graphql-ws close with a fatal 4005 code.
                // Connect without a token instead; the server closes with a
                // retryable 4403 and the next attempt gets a fresh token.
                logger.warn(
                    `[webSocket] Failed to get token for connection: ${err?.message}`
                );
            }
            if (!token) return { fortSessionId: sessionId };
            return {
                authorization: encodeURIComponent(token),
                fortSessionId: sessionId,
            };
        },
        on: {
            closed: handleDisconnected,
            connected: handleConnected,
            error: handleError,
            ping: handlePing,
            pong: handlePong,
        },
    });
    return client;
};

export default graphQlClient;
